import { CASA_LEON_WEATHER_SITE, WEATHER_DATA_SOURCE } from './location.js';
import type { WeatherBundle, WeatherCurrent, WeatherDaily, WeatherHourly } from './weather-types.js';

interface OpenMeteoResponse {
  timezone: string;
  current?: {
    time: string;
    temperature_2m: number;
    apparent_temperature: number;
    precipitation: number;
    weather_code: number;
    wind_speed_10m: number;
    wind_direction_10m: number;
    is_day: number;
  };
  hourly?: {
    time: string[];
    temperature_2m: number[];
    precipitation_probability: number[];
    weather_code: number[];
  };
  daily?: {
    time: string[];
    weather_code: number[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    precipitation_probability_max: number[];
  };
}

export function buildOpenMeteoUrl(): string {
  const { latitude, longitude, timezone } = CASA_LEON_WEATHER_SITE;
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
    timezone,
    forecast_days: '3',
    current: [
      'temperature_2m',
      'apparent_temperature',
      'precipitation',
      'weather_code',
      'wind_speed_10m',
      'wind_direction_10m',
      'is_day',
    ].join(','),
    hourly: 'temperature_2m,precipitation_probability,weather_code',
    daily: 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max',
  });
  return `${WEATHER_DATA_SOURCE.apiBase}?${params.toString()}`;
}

export function parseOpenMeteoResponse(json: OpenMeteoResponse, fetchedAt: string): WeatherBundle {
  const cur = json.current;
  if (!cur) throw new Error('open_meteo_sin_current');

  const current: WeatherCurrent = {
    temperatureC: cur.temperature_2m,
    apparentTemperatureC: cur.apparent_temperature,
    precipitationMm: cur.precipitation,
    weatherCode: cur.weather_code,
    windSpeedKmh: cur.wind_speed_10m,
    windDirectionDeg: cur.wind_direction_10m,
    isDay: cur.is_day === 1,
  };

  const hourly: WeatherHourly[] = [];
  const ht = json.hourly?.time ?? [];
  for (let i = 0; i < Math.min(ht.length, 24); i += 1) {
    hourly.push({
      time: ht[i],
      temperatureC: json.hourly!.temperature_2m[i],
      precipitationProbabilityPct: json.hourly!.precipitation_probability[i],
      weatherCode: json.hourly!.weather_code[i],
    });
  }

  const daily: WeatherDaily[] = [];
  const dt = json.daily?.time ?? [];
  for (let i = 0; i < dt.length; i += 1) {
    daily.push({
      date: dt[i],
      weatherCode: json.daily!.weather_code[i],
      tempMaxC: json.daily!.temperature_2m_max[i],
      tempMinC: json.daily!.temperature_2m_min[i],
      precipitationProbabilityMaxPct: json.daily!.precipitation_probability_max[i],
    });
  }

  return {
    fetchedAt,
    timezone: json.timezone,
    locationLabel: CASA_LEON_WEATHER_SITE.addressLine,
    current,
    hourly,
    daily,
    source: WEATHER_DATA_SOURCE.attribution,
  };
}

export async function fetchOpenMeteo(
  fetchFn: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<WeatherBundle> {
  const url = buildOpenMeteoUrl();
  const res = await fetchFn(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`open_meteo_http_${res.status}`);
  const json = (await res.json()) as OpenMeteoResponse;
  return parseOpenMeteoResponse(json, new Date().toISOString());
}
